import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Users, AlertCircle, MoreVertical, ChevronDown } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useSubmissionUsers,
  type SubmissionUser,
  type SubmissionInvitation,
} from '@/hooks/submissions/useSubmissionUsers';
import { useRoleAssignmentActions } from '@/hooks/submissions/useRoleAssignmentActions';
import { DataTable, DataTableHandle } from '@/components/ui/DataTable';
import {
  ColumnManagerPopover,
  ManagedColumn,
} from '@/components/ui/ColumnManagerPopover';
import { ExportCsvButton } from '@/components/ui/ExportCsvButton';
import {
  useColumnPreferences,
  type ManageableColumnConfig,
} from '@/hooks/useColumnPreferences';
import { useUser } from '@/providers/UserProvider';
import ModifyRoleModal from '../ModifyRoleModal';

type Hierarchy = 'study' | 'site';

interface UserManagementProps {
  /** Legacy: submission ID — used to derive resourcePath if resourcePath is not provided */
  submissionId?: string;
  /** Full resource path prefix, e.g. /studies/123 or /submissions/456 */
  resourcePath?: string;
  canManageUsers?: boolean;
  refreshTrigger?: number;
  /**
   * Which home page the tab is on, when it lists users from below the page's
   * own resource. Omitted on the Submission page.
   * - `study`: groups the role filter by level (Study/Site/Submission) and
   *   adds Site + Submission columns for site-/submission-level users.
   * - `site`: filters by exact role and adds a Submission ID column for
   *   submission-level users (`—` for site-level users).
   */
  hierarchy?: Hierarchy;
  /** Downloaded CSV file name, e.g. 'site-SIT260001-users.csv'. */
  exportFilename?: string;
}

const LEVEL_LABELS: Record<string, string> = {
  study: 'Study-Level',
  site: 'Site-Level',
  submission: 'Submission-Level',
};

// Filter options are ordered top of the hierarchy first, then by key —
// which puts `*_manager` before `*_viewer` within a level.
const LEVEL_ORDER = ['study', 'site', 'submission'];

const levelRank = (level: string | null | undefined): number => {
  const index = level ? LEVEL_ORDER.indexOf(level) : -1;
  return index === -1 ? LEVEL_ORDER.length : index;
};

const EMPTY_STATE_COPY: Record<string, string> = {
  study: 'No users have been assigned to this study yet.',
  site: 'No users have been assigned to this site yet.',
};

// Consistent badge colors per role key — no fake owner/editor/viewer abstraction
const ROLE_BADGE_CLASS: Record<string, string> = {
  study_manager: 'bg-sky-50 text-sky-700 border-sky-200',
  site_manager: 'bg-sky-50 text-sky-700 border-sky-200',
  submission_manager: 'bg-sky-50 text-sky-700 border-sky-200',
  study_viewer: 'bg-neutral-50 text-gray-700 border-gray-200',
  site_viewer: 'bg-neutral-50 text-gray-700 border-gray-200',
  submission_viewer: 'bg-neutral-50 text-gray-700 border-gray-200',
};

const getRoleBadgeClass = (roleKey: string): string =>
  ROLE_BADGE_CLASS[roleKey] ?? 'bg-purple-50 text-purple-700 border-purple-200';

// Merge users and pending invitations into a single row type for DataTable
type UserRow =
  | ({ kind: 'user' } & SubmissionUser)
  | ({ kind: 'invitation' } & SubmissionInvitation);

// Column pinning/visibility/order is saved per page type: the three Users
// tabs have different column sets and must not overwrite each other's
// layout. Not per resource — there is one users table per page and its
// columns are the same on every site (same reasoning as `study-sites`).
const tableIdFor = (hierarchy: Hierarchy | undefined) =>
  `users-${hierarchy ?? 'submission'}`;

const HIERARCHY_COLUMN_IDS: Record<Hierarchy, string[]> = {
  study: ['site', 'submission'],
  site: ['submission'],
};

const manageableColumnsFor = (
  hierarchy: Hierarchy | undefined
): ManageableColumnConfig[] => [
  { id: 'name', defaultVisible: true, defaultPinned: 'left' },
  { id: 'status', defaultVisible: true },
  { id: 'role', defaultVisible: true },
  { id: 'email', defaultVisible: true },
  ...(hierarchy ? HIERARCHY_COLUMN_IDS[hierarchy] : []).map(id => ({
    id,
    defaultVisible: true,
  })),
];

// The row-actions kebab is not user-manageable: it is always pinned right so
// Modify/Remove stay reachable while the table scrolls horizontally.
const ACTIONS_COLUMN_ID = 'actions';

// Associated submission for a submission-level user or invitation, linked to
// its page; `—` for everyone else (site-/study-level rows)
const renderSubmissionCell = (row: UserRow) => {
  if (!row.submission_id) {
    return <span className="text-gray-600">—</span>;
  }
  return (
    <Link
      to={`/submissions/${row.submission_id}`}
      className="text-sky-700 hover:underline"
      onClick={e => e.stopPropagation()}
    >
      {row.submission_display_id ?? row.submission_id}
    </Link>
  );
};

const UserManagement: React.FC<UserManagementProps> = ({
  submissionId,
  resourcePath,
  canManageUsers = false,
  refreshTrigger,
  hierarchy,
  exportFilename = 'users.csv',
}) => {
  const resolvedPath = resourcePath ?? `/submissions/${submissionId}`;

  const { userData } = useUser();
  const { users, invitations, isLoading, error, refetch } =
    useSubmissionUsers(resolvedPath);
  const {
    updateRole,
    deleteRole,
    isLoading: isActionLoading,
  } = useRoleAssignmentActions();
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SubmissionUser | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const dataTableRef = useRef<DataTableHandle>(null);
  const [exportRowCount, setExportRowCount] = useState(0);

  const manageableColumns = useMemo(
    () => manageableColumnsFor(hierarchy),
    [hierarchy]
  );

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    resetToDefaults,
  } = useColumnPreferences({
    tableId: tableIdFor(hierarchy),
    userId: userData?.id,
    columns: manageableColumns,
    defaultOrder: manageableColumns.map(c => c.id),
  });

  // Refetch when refreshTrigger changes (e.g., after invitation is sent)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      refetch();
    }
  }, [refreshTrigger, refetch]);

  // Force cleanup of body styles after Dialog closes
  // This is a workaround for Radix UI Dialog + Select interaction leaving pointer-events: none
  useEffect(() => {
    if (!modifyModalOpen) {
      // Wait longer than Dialog animation (200ms) + Select cleanup
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = '';
        document.body.removeAttribute('data-scroll-locked');
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [modifyModalOpen]);

  const usersArray = useMemo(
    () => (Array.isArray(users) ? users : []),
    [users]
  );
  const invitationsArray = useMemo(
    () => (Array.isArray(invitations) ? invitations : []),
    [invitations]
  );

  // The Study home page filters by role level; every other page by exact role
  const groupByLevel = hierarchy === 'study';
  const filterKeyFor = useCallback(
    (row: SubmissionUser | SubmissionInvitation) =>
      groupByLevel ? row.role_level : row.role_key,
    [groupByLevel]
  );

  // Build filter options from the distinct role names (or, on the Study home
  // page, distinct role levels) actually present in the data — pending
  // invitations included, so a role held only by an invitee is filterable
  const roleFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { key: string; label: string; rank: number }[] = [];
    for (const row of [...usersArray, ...invitationsArray]) {
      const key = filterKeyFor(row);
      const label = groupByLevel
        ? (key && LEVEL_LABELS[key]) || key
        : row.role_name;
      if (key && !seen.has(key)) {
        seen.add(key);
        options.push({
          key,
          label: label ?? key,
          rank: levelRank(row.role_level),
        });
      }
    }
    return options.sort(
      (a, b) => a.rank - b.rank || a.key.localeCompare(b.key)
    );
  }, [usersArray, invitationsArray, filterKeyFor, groupByLevel]);

  const activeFilterLabel =
    roleFilter === 'all'
      ? 'All Users'
      : (roleFilterOptions.find(o => o.key === roleFilter)?.label ??
        'All Users');

  const handleModifyClick = useCallback((user: SubmissionUser) => {
    setSelectedUser(user);
    setModifyModalOpen(true);
    setActionError(null);
  }, []);

  const handleRemoveClick = useCallback(
    async (user: SubmissionUser) => {
      if (!confirm(`Are you sure you want to remove ${user.name}?`)) {
        return;
      }

      try {
        setActionError(null);
        await deleteRole(resolvedPath, user.id);
        await refetch();
      } catch (err) {
        setActionError(
          err instanceof Error ? err.message : 'Failed to remove user'
        );
      }
    },
    [deleteRole, resolvedPath, refetch]
  );

  const handleConfirmModify = async (newRoleKey: string) => {
    if (!selectedUser) return;

    setActionError(null);
    await updateRole(resolvedPath, selectedUser.id, newRoleKey);
    await refetch();
    setModifyModalOpen(false);
    // Don't clear selectedUser - it will be overwritten next time modal opens
  };

  const handleModalOpenChange = (open: boolean) => {
    // Only allow opening if we have a selected user
    if (open && !selectedUser) return;

    setModifyModalOpen(open);
    // Note: We intentionally do NOT clear selectedUser here
    // Keeping it allows the Dialog to animate out properly
    // It will be overwritten when modifying a different user
  };

  // Merge users + pending invitations into a single list for DataTable, both
  // narrowed by the role filter — the CSV export walks these same rows
  const tableRows = useMemo<UserRow[]>(() => {
    const matchesFilter = (row: SubmissionUser | SubmissionInvitation) =>
      roleFilter === 'all' || filterKeyFor(row) === roleFilter;
    return [
      ...usersArray
        .filter(matchesFilter)
        .map(u => ({ kind: 'user' as const, ...u })),
      ...invitationsArray
        .filter(matchesFilter)
        .map(i => ({ kind: 'invitation' as const, ...i })),
    ];
  }, [usersArray, invitationsArray, roleFilter, filterKeyFor]);

  const columns = useMemo<ColumnDef<UserRow, unknown>[]>(
    () => [
      {
        header: 'Name',
        id: 'name',
        accessorFn: (row: UserRow) =>
          row.kind === 'user' ? row.name : (row.name ?? row.email),
        enableSorting: true,
        meta: { overflow: 'truncate', width: 200 },
        cell: ({ row }: { row: { original: UserRow } }) => {
          const name =
            row.original.kind === 'user'
              ? row.original.name
              : (row.original.name ?? row.original.email);
          const isPending = row.original.kind === 'invitation';
          return (
            <span
              className={`font-medium text-gray-900 ${isPending ? 'opacity-60' : ''}`}
            >
              {name}
            </span>
          );
        },
      },
      {
        header: 'Status',
        id: 'status',
        accessorFn: (row: UserRow) =>
          row.kind === 'invitation' ? 'Pending' : 'Active',
        enableSorting: true,
        meta: { overflow: 'nowrap', width: 110 },
        cell: ({ row }: { row: { original: UserRow } }) =>
          row.original.kind === 'invitation' ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border whitespace-nowrap bg-neutral-50 text-gray-500 border-gray-200">
              Pending
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border whitespace-nowrap bg-green-50 text-green-700 border-green-200">
              Active
            </span>
          ),
      },
      {
        header: 'Role',
        id: 'role',
        accessorFn: (row: UserRow) => row.role_name,
        enableSorting: true,
        meta: { overflow: 'nowrap', width: 180 },
        cell: ({ row }: { row: { original: UserRow } }) => {
          const isPending = row.original.kind === 'invitation';
          const badgeClass = isPending
            ? 'bg-neutral-50 text-gray-500 border-gray-200'
            : getRoleBadgeClass(row.original.role_key);
          return (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border whitespace-nowrap ${badgeClass} ${isPending ? 'opacity-60' : ''}`}
            >
              {row.original.role_name}
            </span>
          );
        },
      },
      {
        header: 'Email',
        id: 'email',
        accessorFn: (row: UserRow) => row.email,
        enableSorting: true,
        meta: { overflow: 'truncate', width: 260 },
        cell: ({ row }: { row: { original: UserRow } }) => (
          <span
            className={`text-gray-600 ${row.original.kind === 'invitation' ? 'opacity-60' : ''}`}
          >
            {row.original.email}
          </span>
        ),
      },
      ...(hierarchy === 'study'
        ? ([
            {
              header: 'Site',
              id: 'site',
              accessorFn: (row: UserRow) =>
                row.kind === 'user' ? row.site_name : null,
              enableSorting: true,
              meta: { overflow: 'truncate', width: 180 },
              cell: ({ row }: { row: { original: UserRow } }) => (
                <span className="text-gray-600">
                  {row.original.kind === 'user'
                    ? (row.original.site_name ?? '—')
                    : '—'}
                </span>
              ),
            },
          ] as ColumnDef<UserRow, unknown>[])
        : []),
      ...(hierarchy
        ? ([
            {
              header: hierarchy === 'site' ? 'Submission ID' : 'Submission',
              id: 'submission',
              accessorFn: (row: UserRow) => row.submission_display_id ?? null,
              enableSorting: true,
              meta: { overflow: 'truncate', width: 160 },
              cell: ({ row }: { row: { original: UserRow } }) =>
                renderSubmissionCell(row.original),
            },
          ] as ColumnDef<UserRow, unknown>[])
        : []),
      {
        id: ACTIONS_COLUMN_ID,
        header: '',
        enableSorting: false,
        meta: { overflow: 'nowrap', width: 56, exportable: false },
        cell: ({ row }: { row: { original: UserRow } }) => {
          if (row.original.kind === 'invitation') {
            return (
              <span className="inline-flex p-1.5 text-gray-300">
                <MoreVertical className="w-5 h-5" />
              </span>
            );
          }
          const user = row.original;
          return canManageUsers ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="text-gray-400 hover:text-gray-600 cursor-pointer p-1.5"
                  disabled={isActionLoading}
                  onClick={e => e.stopPropagation()}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleModifyClick(user)}>
                  Modify
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleRemoveClick(user)}
                  className="text-red-600 focus:text-red-600"
                >
                  Remove
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button className="text-gray-400 cursor-default p-1.5">
              <MoreVertical className="w-5 h-5" />
            </button>
          );
        },
      },
    ],
    [
      canManageUsers,
      isActionLoading,
      handleModifyClick,
      handleRemoveClick,
      hierarchy,
    ]
  );

  const managedColumns: ManagedColumn[] = useMemo(() => {
    const labels: Record<string, string> = {
      name: 'Name',
      status: 'Status',
      role: 'Role',
      email: 'Email',
      site: 'Site',
      submission: hierarchy === 'site' ? 'Submission ID' : 'Submission',
    };
    return manageableColumns.map(({ id }) => ({ id, label: labels[id] }));
  }, [manageableColumns, hierarchy]);

  const hasRows = usersArray.length > 0 || invitationsArray.length > 0;

  // Only the first load shows a skeleton. A refetch (after invite / modify /
  // remove) keeps the current rows on screen, so the toolbar and the table's
  // sort don't unmount under the user
  if (isLoading && !hasRows) {
    return (
      <div className="min-h-0">
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-0">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!hasRows) {
    return (
      <div className="min-h-0">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No users found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {(hierarchy && EMPTY_STATE_COPY[hierarchy]) ??
                'No users have been assigned to this submission yet.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-0">
      {actionError && (
        <div className="mb-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50 transition-colors cursor-pointer text-sm"
            >
              <span>{activeFilterLabel}</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onClick={() => setRoleFilter('all')}
              className="cursor-pointer"
            >
              All Users
            </DropdownMenuItem>
            {roleFilterOptions.map(opt => (
              <DropdownMenuItem
                key={opt.key}
                onClick={() => setRoleFilter(opt.key)}
                className="cursor-pointer"
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="ml-auto flex items-center gap-2">
          <ExportCsvButton
            dataTableRef={dataTableRef}
            filename={exportFilename}
            rowCount={exportRowCount}
          />
          <ColumnManagerPopover
            columns={managedColumns}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            columnOrder={columnOrder}
            onColumnOrderChange={setColumnOrder}
            columnPinning={columnPinning}
            onColumnPinningChange={setColumnPinning}
            onReset={resetToDefaults}
          />
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <DataTable
          ref={dataTableRef}
          columns={columns}
          data={tableRows}
          emptyMessage="No users found."
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnOrder={[...columnOrder, ACTIONS_COLUMN_ID]}
          onColumnOrderChange={setColumnOrder}
          columnPinning={{
            ...columnPinning,
            right: [...(columnPinning.right ?? []), ACTIONS_COLUMN_ID],
          }}
          onColumnPinningChange={setColumnPinning}
          onExportRowCountChange={setExportRowCount}
        />
      </div>

      <ModifyRoleModal
        open={modifyModalOpen}
        onOpenChange={handleModalOpenChange}
        resourcePath={resolvedPath}
        userName={selectedUser?.name || ''}
        currentRoleKey={selectedUser?.role_key || ''}
        currentRoleName={selectedUser?.role_name || ''}
        roles={selectedUser?.assignable_roles}
        onConfirm={handleConfirmModify}
        isLoading={isActionLoading}
      />
    </div>
  );
};

export default UserManagement;
